import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { BudgetGoalRow, GoalsSplitBar } from '@/components/budget';
import { ReadingError } from '@/components/dashboard';
import { Button } from '@/components/ui/button';
import { useBudgetGoals, useUpdateBudgetGoals } from '@/hooks/useBudgets';
import type { BudgetGoal } from '@/lib/api';
import { BUDGET_TYPES, ROUTES, type BudgetType } from '@/lib/constants';
import { formatPercent } from '@/lib/format';
import {
  BUDGET_GOALS_COMPLETE,
  BUDGET_GOALS_DISCARD,
  BUDGET_GOALS_LABEL,
  BUDGET_GOALS_LIST_HEADING,
  BUDGET_GOALS_LOADING,
  BUDGET_GOALS_LOAD_ERROR_TITLE,
  BUDGET_GOALS_PAGE_EYEBROW,
  BUDGET_GOALS_SAVE,
  BUDGET_GOALS_SAVING,
  BUDGET_GOALS_STATUS,
  BUDGET_GOALS_TOTAL_LABEL,
  BUDGET_VIEW_BUDGET,
  budgetGoalsClose,
  budgetGoalsOver,
  budgetGoalsShort,
  translateBudgetType,
} from '@/lib/translations';
import { cn } from '@/lib/utils';
import {
  closingAdjustment,
  draftGoals,
  goalsBalance,
  pruneGoalEdits,
  setGoalEdit,
  sumHundredths,
  toHundredths,
  type GoalEdits,
} from '@/lib/utils/budget';

interface GoalsEditorProps {
  saved: BudgetGoal[];
}

// The page proper, once the saved goals are here. It owns one thing: the edits, as an overlay on the saved set
// (only the buckets the user moved, in hundredths — see GoalEdits). Everything else is derived: the draft, the
// total and what it lacks, and whether there is anything to save.
//
// The only arithmetic here is over that unsaved draft — its running total — because the server cannot answer
// "what do these six sliders add up to?" once per pointer move. It mirrors a rule the server enforces anyway (a
// save that doesn't add up to 100 is a 422), and no money is computed: amounts are the backend's, on Orçamento.
const GoalsEditor: React.FC<GoalsEditorProps> = ({ saved }) => {
  const [edits, setEdits] = useState<GoalEdits>({});
  // The group the user moved last: the closing shortcut leaves it alone, since that edit is the point.
  const [lastTouched, setLastTouched] = useState<BudgetType>();
  const save = useUpdateBudgetGoals();
  const isSaving = save.isPending;

  // An edit the saved set has caught up with — the response to a save, a refetch — stops being one. Derived here
  // so the frame that shows a save's response is already clean, and written back below so that a later change to
  // the saved set can't bring a spent edit back.
  const activeEdits = useMemo(() => pruneGoalEdits(edits, saved), [edits, saved]);
  useEffect(() => {
    setEdits((current) => {
      const pruned = pruneGoalEdits(current, saved);
      return Object.keys(pruned).length === Object.keys(current).length ? current : pruned;
    });
  }, [saved]);

  const draft = draftGoals(saved, activeEdits);
  const total = sumHundredths(draft);
  const balance = goalsBalance(total);
  const isDirty = Object.keys(activeEdits).length > 0;
  // The save gate is exact: Σ hundredths === 10000, never a float sum (§10.2). Each goal is 0–100 by
  // construction — the sliders can't leave that range.
  const canSave = isDirty && balance.kind === 'complete' && !isSaving;
  const canDiscard = isDirty && !isSaving;

  // While a save is in flight the sliders ignore input rather than disabling: a focused control that becomes
  // disabled drops keyboard focus, and an edit made mid-flight would race the response that is about to replace
  // the saved set.
  const handleChange = (type: BudgetType, hundredths: number) => {
    if (isSaving) return;
    setLastTouched(type);
    setEdits((current) => setGoalEdit(current, saved, type, hundredths));
  };
  const handleDiscard = () => {
    if (canDiscard) setEdits({});
  };
  const handleSave = () => {
    if (canSave) save.mutate(draft);
  };

  // Short or over: one press settles the gap on the largest other group (named, with its new value).
  const closing = balance.kind === 'complete' || isSaving ? null : closingAdjustment(draft, lastTouched);
  const handleClose = () => {
    if (closing) setEdits((current) => setGoalEdit(current, saved, closing.type, closing.hundredths));
  };

  const verdict =
    balance.kind === 'complete'
      ? BUDGET_GOALS_COMPLETE
      : balance.kind === 'short'
        ? budgetGoalsShort(balance.hundredths)
        : budgetGoalsOver(balance.hundredths);

  return (
    <div className="max-w-3xl">
      {/* Sticky, so the running total stays in view while a slider further down the list moves it. Below `lg` it parks
          at 56px, 1px under the fixed mobile header (57px: py-3 around a 32px row, plus its border) — not at the 64px
          the page's `pt-16` reserves, which would leave a band for the rows to scroll through. */}
      <div className="sticky top-14 z-30 border-y border-border/60 bg-background py-3 lg:top-0">
        <div className="flex items-center justify-between gap-x-4">
          <p className="flex items-baseline gap-x-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              {BUDGET_GOALS_TOTAL_LABEL}
            </span>
            <span className="font-display text-[clamp(1.4rem,3.2vw,1.875rem)] font-bold leading-[1.05] tracking-tight tabular-nums text-foreground">
              {formatPercent(total / 100)}
            </span>
          </p>
          {/* Neutral either way (Semantic Color Rule): a goal set that doesn't add up is not an expense. Quiet when
              all is well, plain foreground when there is something to fix. Not a live region: its figure changes on
              every slider step, and the slider already speaks its own value — the status below says only the state. */}
          <p
            className={cn(
              'flex items-center gap-1.5 text-sm',
              balance.kind === 'complete' ? 'text-muted-foreground' : 'font-medium text-foreground'
            )}
          >
            {balance.kind === 'complete' && <Check className="h-4 w-4" aria-hidden="true" />}
            {verdict}
          </p>
        </div>
        {/* Announced when the state changes (short → over → complete), not on every hundredth along the way. */}
        <p role="status" className="sr-only">
          {BUDGET_GOALS_STATUS[balance.kind]}
        </p>
        <GoalsSplitBar goals={draft} className="mt-3" />
        {closing && (
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="-mr-3 h-11 text-muted-foreground hover:bg-muted hover:text-foreground sm:h-9"
            >
              {budgetGoalsClose(translateBudgetType(closing.type), closing.hundredths)}
            </Button>
          </div>
        )}
      </div>

      <section aria-labelledby="goals-list-heading">
        <h2 id="goals-list-heading" className="sr-only">
          {BUDGET_GOALS_LIST_HEADING}
        </h2>
        <ul aria-busy={isSaving} className={cn('divide-y divide-border/50 transition-opacity', isSaving && 'opacity-70')}>
          {draft.map((goal) => (
            <BudgetGoalRow
              key={goal.type}
              type={goal.type}
              hundredths={toHundredths(goal.percentage)}
              onChange={(next) => handleChange(goal.type, next)}
            />
          ))}
        </ul>
      </section>

      {/* Below `sm` the actions ride along the bottom of the screen, so Salvar is reachable from any row — the
          mirror of the sticky total above; from `sm` the list fits and the footer sits in the flow. The page's own
          gutter is cancelled so the bar's hairline and background run edge to edge. */}
      <div className="sticky bottom-0 z-30 -mx-4 flex items-center justify-end gap-3 border-t border-border/60 bg-background px-4 py-3 sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:pt-4">
        {/* aria-disabled, not disabled: pressing Salvar turns it inert the moment the save starts, and a disabled
            button would take keyboard focus with it. The handlers guard the same conditions. */}
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleDiscard}
            aria-disabled={!canDiscard}
            className={cn('text-muted-foreground hover:bg-muted hover:text-foreground', !canDiscard && 'pointer-events-none opacity-50')}
          >
            {BUDGET_GOALS_DISCARD}
          </Button>
          {/* The screen's one amber element, and only while there is something valid to save (§10.4). */}
          <Button
            type="button"
            variant={canSave || isSaving ? 'default' : 'secondary'}
            onClick={handleSave}
            aria-disabled={!canSave}
            aria-busy={isSaving}
            className={cn(!canSave && 'pointer-events-none opacity-50')}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                {BUDGET_GOALS_SAVING}
              </>
            ) : (
              BUDGET_GOALS_SAVE
            )}
          </Button>
        </div>
      </div>
      {/* The way on to Orçamento, where a saved goal shows up as money: the mirror of its "Ajustar metas". Under the
          actions, not in the bar — at 320px there is no room beside two buttons — and not in the PageHeader, whose
          action slot is for a page's primary button (Salvar is this page's). -ml-4 lines its text up with the rows. */}
      <Button
        variant="ghost"
        asChild
        className="-ml-4 mt-2 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Link to={ROUTES.dashboardBudget}>
          {BUDGET_VIEW_BUDGET}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </div>
  );
};

// The editor's shape while the goals load — the total strip and six rows — so nothing jumps when they land.
const GoalsSkeleton: React.FC = () => (
  <div className="max-w-3xl">
    <p role="status" className="sr-only">
      {BUDGET_GOALS_LOADING}
    </p>
    <div aria-hidden="true">
      <div className="border-y border-border/60 py-3">
        <div className="flex items-center justify-between">
          <div className="h-8 w-40 animate-pulse rounded bg-muted" />
          <div className="h-4 w-28 animate-pulse rounded bg-muted" />
        </div>
        <div className="mt-3 h-2 animate-pulse rounded-full bg-muted" />
      </div>
      <ul className="divide-y divide-border/50">
        {BUDGET_TYPES.map((type) => (
          <li key={type} className="py-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1.5">
                <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                <div className="h-3 w-56 max-w-full animate-pulse rounded bg-muted" />
              </div>
              <div className="h-5 w-14 animate-pulse rounded bg-muted" />
            </div>
            <div className="mt-4 h-1.5 animate-pulse rounded-full bg-muted" />
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const Goals: React.FC = () => {
  const goalsQuery = useBudgetGoals();
  const goals = goalsQuery.data?.goals;

  const renderContent = () => {
    if (goalsQuery.isPending) return <GoalsSkeleton />;

    // An inline strip, as on Painel: "something broke" never reads like "nothing here yet", and its retry is quiet
    // (the page's amber belongs to Salvar).
    if (!goals) {
      return (
        <div className="max-w-3xl">
          <ReadingError
            headingLevel="h2"
            title={BUDGET_GOALS_LOAD_ERROR_TITLE}
            error={goalsQuery.error}
            onRetry={() => void goalsQuery.refetch()}
          />
        </div>
      );
    }

    return <GoalsEditor saved={goals} />;
  };

  return (
    <Layout>
      <div className="space-y-8">
        <PageHeader title={BUDGET_GOALS_LABEL} eyebrow={BUDGET_GOALS_PAGE_EYEBROW} />

        {renderContent()}
      </div>
    </Layout>
  );
};

export default Goals;
